import { useEffect, useState, useRef } from 'react';
import { Link, NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { getRole, logout, authHeaders } from '../../utils/auth';
import { apiFetch } from '../../utils/api';
import { BatchProvider } from '../../context/BatchProvider';
import { SelectionContext } from '../../context/SelectionContext';
import mbLogo from '../../assets/milwaukee-ballet-logo.png';
import agmaMark from '../../assets/agma-mark.png';


const adminNav = [
    { to: '/admin/photos',    label: 'Photos' },
    { to: '/admin/audit-log', label: 'Audit Log' },
    { to: '/admin/users',     label: 'Users' },
];

const adminDrawerNav = [
    { to: '/admin/photos',    label: 'Photos' },
    { to: '/admin/audit-log', label: 'Audit Log' },
    { to: '/admin/users',     label: 'Users' },
];

const adDrawerNav: { to: string; label: string }[] = [];

const artistDrawerNav = [
    { to: "/portfolio?tab=my-photos",      label: "My Photos" },
    { to: "/portfolio?tab=group-photos",   label: "Group Photos" },
    { to: "/portfolio?tab=gallery",        label: "Galleries" },
];

const artistNav = [
    { to: '/portfolio',      label: 'My Portfolio' },
];

function HamburgerIcon() {
    return (
        <svg width='22' height='22' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round'>
            <line x1='3' y1='6' x2='21' y2='6' />
            <line x1='3' y1='12' x2='21' y2='12' />
            <line x1='3' y1='18' x2='21' y2='18' />
        </svg>
    );
}

function CloseIcon() {
    return (
        <svg width='22' height='22' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round'>
            <line x1='18' y1='6' x2='6' y2='18' />
            <line x1='6' y1='6' x2='18' y2='18' />
        </svg>
    );
}

export default function AppShell() {
    const location = useLocation();
    const role = getRole();
    const navigate = useNavigate();
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [adSelectedIds, setAdSelectedIds] = useState<Set<number>>(new Set());
    const [shellError, setShellError] = useState('');
    const headerRef = useRef<HTMLElement>(null);

    const [showReviewTab, setShowReviewTab] = useState(false);
    const [hasNewGalleries, setHasNewGalleries] = useState(false);

    const nav = role === 'admin' ? adminNav
        : role === 'artist' ? artistNav
        : [];

    const artistDrawerNavDynamic = [
        ...artistDrawerNav,
        ...(showReviewTab ? [{ to: '/portfolio?tab=review-gallery', label: 'Secondary Review' }] : []),
    ];

    const drawerNav = role === 'admin' ? adminDrawerNav
        : role === 'artistic_director' ? adDrawerNav
        : role === 'artist' ? artistDrawerNavDynamic
        : [];

    useEffect(() => {
        if (role !== 'artist') return;
        apiFetch('/api/gallery/window-status', { headers: authHeaders() })
            .then(r => r.json())
            .then((d: { active: boolean }) => setShowReviewTab(d.active === true))
            .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const SEEN_KEY = 'prosceno_gallery_seen_ids';

    const checkGalleryBadge = () => {
        if (role !== 'artist') return;
        apiFetch('/api/gallery/released', { headers: authHeaders() })
            .then(r => r.ok ? r.json() : { data: [] })
            .then((body: { data: { batchId: number }[] }) => {
                const released = (body.data ?? []).map((b: { batchId: number }) => b.batchId);
                if (released.length === 0) { setHasNewGalleries(false); return; }
                try {
                    const seen: number[] = JSON.parse(localStorage.getItem(SEEN_KEY) ?? '[]');
                    const hasNew = released.some(id => !seen.includes(id));
                    setHasNewGalleries(hasNew);
                } catch { setHasNewGalleries(true); }
            })
            .catch(() => {});
    };

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    useEffect(() => {
        if (!drawerOpen) return;
        document.body.style.overflow = 'hidden';
        const handleKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') setDrawerOpen(false); };
        window.addEventListener('keydown', handleKeyDown);
        return () => {
            document.body.style.overflow = '';
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [drawerOpen]);

    // Check gallery notification badge on mount and on every navigation
    useEffect(() => {
        checkGalleryBadge();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname, location.search]);

    useEffect(() => {
        const header = headerRef.current;
        if (!header) return;
        const setHeaderHeight = () => {
            document.documentElement.style.setProperty('--app-header-height', `${header.offsetHeight}px`);
        };
        setHeaderHeight();
        const observer = new ResizeObserver(setHeaderHeight);
        observer.observe(header);
        return () => observer.disconnect();
    }, []);


    return (
        <SelectionContext.Provider value={{ adSelectedIds, setAdSelectedIds }}>
        <div className='app-shell'>
            <header className='app-header' ref={headerRef}>
                <div className='app-header-brand'>
                    <img src={mbLogo} alt='Milwaukee Ballet' className='app-logo' />
                    <h1 className='app-title'>PHOTO APPROVAL</h1>
                    {role && (
                        <span className='app-role'>
                            {role === 'admin' ? 'Admin'
                                : role === 'artistic_director' ? 'Artistic Staff'
                                : 'Artist'}
                        </span>
                    )}
                </div>

                <nav className='app-nav'>
                    {nav.map((item) => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            className={({ isActive }) => `app-nav-link${isActive ? ' active' : ''}`}
                        >
                            {item.label}
                        </NavLink>
                    ))}
                </nav>

                <div className='app-header-account'>
                    <Link to='/change-password' className='app-nav-btn' style={{ textDecoration: 'none' }}>My Account</Link>
                    <button onClick={handleLogout} className='app-nav-btn'>Log out</button>
                </div>

                {(role === 'admin' || role === 'artistic_director' || role === 'artist') && (
                    <button
                        type='button'
                        className='app-nav-hamburger'
                        aria-label='Open menu'
                        aria-expanded={drawerOpen}
                        onClick={() => setDrawerOpen(true)}
                        style={{ position: 'relative' }}
                    >
                        <HamburgerIcon />
                        {role === 'artist' && (hasNewGalleries || showReviewTab) && (
                            <span style={{
                                position: 'absolute',
                                top: 2,
                                right: 2,
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                background: 'var(--color-accent)',
                                pointerEvents: 'none',
                            }} aria-hidden='true' />
                        )}
                    </button>
                )}
            </header>

            {shellError && (
                <div className='shell-error-banner'>
                    {shellError}
                    <button className='inline-error-dismiss' onClick={() => setShellError('')}>×</button>
                </div>
            )}

            {(role === 'admin' || role === 'artistic_director' || role === 'artist') && (
                    <>
                        <div
                            className={`app-drawer-overlay${drawerOpen ? ' open' : ''}`}
                            onClick={() => setDrawerOpen(false)}
                        />
                        <div
                            className={`app-drawer${drawerOpen ? ' open' : ''}`}
                            role='dialog'
                            aria-modal='true'
                            aria-label='Menu'
                        >
                            <button
                                type='button'
                                className='app-drawer-close'
                                aria-label='Close menu'
                                onClick={() => setDrawerOpen(false)}
                            >
                                <CloseIcon />
                            </button>
                            <nav className='app-drawer-nav'>
                                {drawerNav.map((item) => {
                                    const currentHref = location.pathname + location.search;
                                    const isActive = role === 'artist'
                                        ? currentHref === item.to
                                        : location.pathname === item.to;
                                    const showBadge =
                                        (item.to === '/portfolio?tab=gallery' && hasNewGalleries) ||
                                        (item.to === '/portfolio?tab=review-gallery' && showReviewTab);
                                    return (
                                        <Link
                                            key={item.to}
                                            to={item.to}
                                            className={`app-drawer-link${isActive ? ' active' : ''}`}
                                            onClick={() => setDrawerOpen(false)}
                                            style={{ position: 'relative' }}
                                        >
                                            {item.label}
                                            {showBadge && (
                                                <span style={{
                                                    display: 'inline-block',
                                                    width: 8,
                                                    height: 8,
                                                    borderRadius: '50%',
                                                    background: 'var(--color-accent)',
                                                    marginLeft: 7,
                                                    verticalAlign: 'middle',
                                                    flexShrink: 0,
                                                }} aria-hidden='true' />
                                            )}
                                        </Link>
                                    );
                                })}
                                {role === 'artist' && (
                                    <>
                                        <Link to='/change-password' className='app-drawer-link' onClick={() => setDrawerOpen(false)}>
                                            My Account
                                        </Link>
                                        <button
                                            type='button'
                                            onClick={() => { setDrawerOpen(false); handleLogout(); }}
                                            className='app-drawer-link app-drawer-logout'
                                        >
                                            Log out
                                        </button>
                                    </>
                                )}
                            </nav>
                            {(role === 'admin' || role === 'artistic_director') && (
                                <div className='app-drawer-account'>
                                    <Link to='/change-password' className='app-drawer-link' onClick={() => setDrawerOpen(false)}>
                                        My Account
                                    </Link>
                                    <button
                                        type='button'
                                        onClick={() => { setDrawerOpen(false); handleLogout(); }}
                                        className='app-drawer-link app-drawer-logout'
                                    >
                                        Log out
                                    </button>
                                </div>
                            )}
                        </div>
                    </>
                )}

            <main className='app-main'>
                <BatchProvider>
                    <Outlet />
                </BatchProvider>
            </main>

            <footer className='app-footer'>
                <img src={agmaMark} alt='AGMA' className='app-footer-mark' />
                <span className='app-footer-text'>
                    This system implements the photo approval rights guaranteed under
                    AGMA Collective Bargaining Agreement, Section 4, Article 41.
                </span>
            </footer>
        </div>
        </SelectionContext.Provider>
    );
}
